import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authenticatedFetch } from '@/lib/auth';
import { ClipboardList, AlertCircle, CheckCircle, Loader2, Save } from 'lucide-react';
import { useToast, useConfirm } from './ui';

interface Question {
  id: string;
  question_text: string;
  question_type: 'multiple_choice' | 'true_false' | 'short_answer' | 'essay';
  points: number;
  answer_id?: string;
  candidate_answer?: string;
  sensor_score?: number;
  sensor_comment?: string;
}

interface SessionInfo {
  id: string;
  unique_candidate_id: string;
  candidate_code: string;
  course_name: string;
  accommodation_type: string;
  accommodation_description: string;
  accommodation_id: string;
}

export default function SensorManualEntry() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm } = useConfirm();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [session, setSession] = useState<SessionInfo | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, { text: string; score: number; comment: string }>>({});

  useEffect(() => {
    loadSessionData();
  }, [sessionId]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await authenticatedFetch(`/api/sensor/manual-entry/${sessionId}`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Kunne ikke laste øktdata');
      }

      setSession(data.session);
      setQuestions(data.questions);

      // Pre-populate answers if they exist
      const initialAnswers: Record<string, { text: string; score: number; comment: string }> = {};
      data.questions.forEach((q: Question) => {
        if (q.answer_id) {
          initialAnswers[q.id] = {
            text: q.candidate_answer || '',
            score: q.sensor_score || 0,
            comment: q.sensor_comment || ''
          };
        } else {
          initialAnswers[q.id] = {
            text: '',
            score: 0,
            comment: ''
          };
        }
      });
      setAnswers(initialAnswers);

    } catch (err: any) {
      console.error('Load session error:', err);
      setError(err.message || 'Kunne ikke laste øktdata');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, field: 'text' | 'score' | 'comment', value: string | number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
  };

  const saveAnswer = async (questionId: string) => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const answer = answers[questionId];
      if (!answer.text.trim()) {
        throw new Error('Kandidatens svar kan ikke være tomt');
      }

      const response = await authenticatedFetch(`/api/sensor/manual-entry/${sessionId}/answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: questionId,
          candidate_answer: answer.text,
          sensor_score: answer.score,
          sensor_comment: answer.comment || null
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Kunne ikke lagre svar');
      }

      setSuccess('Svar lagret');
      setTimeout(() => setSuccess(''), 3000);

      // Reload to get updated answer IDs
      await loadSessionData();

    } catch (err: any) {
      console.error('Save answer error:', err);
      setError(err.message || 'Kunne ikke lagre svar');
    } finally {
      setSaving(false);
    }
  };

  const finalizeGrading = async () => {
    try {
      // Check that all questions have answers
      const unanswered = questions.filter(q => !answers[q.id]?.text?.trim());
      if (unanswered.length > 0) {
        setError(`${unanswered.length} spørsmål mangler svar. Alle spørsmål må besvares før sensur kan fullføres.`);
        return;
      }

      // Check that all answers are saved
      const unsaved = questions.filter(q => !q.answer_id);
      if (unsaved.length > 0) {
        setError(`${unsaved.length} svar er ikke lagret. Lagre alle svar før du fullfører sensur.`);
        return;
      }

      const confirmed = await confirm({
        title: 'Fullfør sensur',
        message: 'Er du sikker på at du vil fullføre sensuren? Dette kan ikke angres.',
        confirmLabel: 'Fullfør',
        cancelLabel: 'Avbryt',
        variant: 'warning'
      });

      if (!confirmed) {
        return;
      }

      setFinalizing(true);
      setError('');

      const response = await authenticatedFetch(`/api/sensor/manual-entry/${sessionId}/finalize`, {
        method: 'POST'
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Kunne ikke fullføre sensur');
      }

      toast.showSuccess(`Sensur fullført! Totalt: ${data.result.total_points}/${data.result.max_points} poeng (${data.result.percentage}%) - Karakter: ${data.result.grade} - ${data.result.passed ? 'Bestått' : 'Ikke bestått'}`);

      // Navigate back to sensor dashboard or session review
      navigate(`/sensor/sessions/${sessionId}/review`);

    } catch (err: any) {
      console.error('Finalize error:', err);
      setError(err.message || 'Kunne ikke fullføre sensur');
    } finally {
      setFinalizing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card">
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="h-6 w-6" />
            <p>Økten ble ikke funnet</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
          <ClipboardList className="h-8 w-8 text-kf-primary" />
          Manuell sensur - Muntlig prøve
        </h1>
        <p className="text-gray-600">Registrer kandidatens muntlige svar og gi poeng</p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Session Info */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold mb-4">Øktinformasjon</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Kandidatkode</p>
            <p className="font-mono font-semibold">{session.candidate_code}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Kurs</p>
            <p className="font-semibold">{session.course_name}</p>
          </div>
          <div className="col-span-2">
            <p className="text-sm text-gray-600">Tilrettelegging</p>
            <p className="font-semibold">{session.accommodation_type}</p>
            {session.accommodation_description && (
              <p className="text-sm text-gray-700 mt-1">{session.accommodation_description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-6 mb-6">
        {questions.map((question, index) => {
          const answer = answers[question.id] || { text: '', score: 0, comment: '' };
          const isSaved = !!question.answer_id;

          return (
            <div key={question.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-lg">Spørsmål {index + 1}</span>
                    <span className="text-sm bg-kf-primary/10 text-kf-primary px-2 py-1 rounded">
                      {question.points} poeng
                    </span>
                    {isSaved && (
                      <span className="text-sm bg-green-100 text-green-700 px-2 py-1 rounded flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Lagret
                      </span>
                    )}
                  </div>
                  <p className="text-gray-800">{question.question_text}</p>
                </div>
              </div>

              {/* Candidate Answer */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kandidatens muntlige svar (skriv inn det kandidaten sa)
                </label>
                <textarea
                  value={answer.text}
                  onChange={(e) => handleAnswerChange(question.id, 'text', e.target.value)}
                  className="input min-h-[120px]"
                  placeholder="Skriv inn kandidatens muntlige svar her..."
                />
              </div>

              {/* Score */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Poeng (maks {question.points})
                </label>
                <input
                  type="number"
                  min="0"
                  max={question.points}
                  step="0.5"
                  value={answer.score}
                  onChange={(e) => handleAnswerChange(question.id, 'score', parseFloat(e.target.value) || 0)}
                  className="input w-32"
                />
              </div>

              {/* Sensor Comment */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kommentar til kandidaten (valgfritt)
                </label>
                <textarea
                  value={answer.comment}
                  onChange={(e) => handleAnswerChange(question.id, 'comment', e.target.value)}
                  className="input min-h-[80px]"
                  placeholder="Begrunnelse for poenggivning..."
                />
              </div>

              {/* Save Button */}
              <div className="flex justify-end">
                <button
                  onClick={() => saveAnswer(question.id)}
                  disabled={saving || !answer.text.trim()}
                  className="btn-primary flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Lagrer...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Lagre svar
                    </>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Finalize Button */}
      <div className="card bg-kf-primary/5 border-kf-primary">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-1">Fullfør sensur</h3>
            <p className="text-sm text-gray-600">
              Alle svar må være lagret før sensuren kan fullføres. Karakteren blir automatisk beregnet.
            </p>
          </div>
          <button
            onClick={finalizeGrading}
            disabled={finalizing || questions.some(q => !q.answer_id)}
            className="btn-primary bg-green-600 hover:bg-green-700 flex items-center gap-2"
          >
            {finalizing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Fullfører...
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                Fullfør sensur
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
